import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthAPI } from "../api";
import { useAuth } from "../context/AuthContext";

const linkBase =
  "rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A]";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isValidating, logout } = useAuth();

  const isLoggedIn = isAuthenticated;
  const username = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username
    : null;
  const isMentor = user?.is_mentor === true;

  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userMenuRef = useRef(null);

  const primaryLinks = useMemo(() => {
    if (!isLoggedIn) return [];
    const links = [{ to: "/calendar", label: "My Calendar" }];
    if (isMentor) links.push({ to: "/dashboard", label: "Dashboard" });
    if (!isMentor) links.push({ to: "/mentors", label: "Mentors" });
    return links;
  }, [isLoggedIn, isMentor]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onOutsideClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, [menuOpen]);

  useEffect(() => {
    const onEsc = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const loadNotifications = async () => {
      try {
        const res = await AuthAPI.get("notifications/");
        setUnreadCount(res.data.filter((n) => !n.is_read).length);
      } catch (err) {
        console.error("Failed to load notifications", err);
      }
    };

    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [isLoggedIn, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavLink = ({ to, children, className = "" }) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`${linkBase} ${
          isActive
            ? "bg-[#eef0fa] text-[#282E6A]"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        } ${className}`}
      >
        {children}
      </Link>
    );
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2">
          <img src="/logo.svg" alt="Hindsight" className="h-10 w-auto sm:h-11" />
          <span className="text-lg font-semibold text-[#282E6A]">Hindsight</span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {primaryLinks.map((item) => (
            <NavLink key={item.to} to={item.to}>
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {!isLoggedIn ? (
            <div className="hidden items-center gap-2 md:flex">
              <Link
                to="/login"
                className="px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="rounded-lg bg-[#282E6A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1e2458]"
              >
                Get started
              </Link>
            </div>
          ) : (
            <>
              <Link
                to="/notifications"
                className="relative rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A]"
                aria-label="Notifications"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Link>

              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A]"
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                >
                  {isValidating && !username ? (
                    <span className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                  ) : (
                    <span className="font-medium text-gray-800">{username}</span>
                  )}
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1.5 w-52 rounded-xl border border-gray-100 bg-white py-1.5 shadow-lg" role="menu">
                    <Link to="/mybookings" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" role="menuitem">
                      My Bookings
                    </Link>
                    <Link to="/editprofile" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" role="menuitem">
                      Edit Profile
                    </Link>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"
                      role="menuitem"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="rounded-md p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#282E6A] md:hidden"
            aria-expanded={mobileOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-gray-200 bg-white px-4 py-3 md:hidden">
          <div className="flex flex-col gap-1">
            {primaryLinks.map((item) => (
              <NavLink key={item.to} to={item.to} className="w-full">
                {item.label}
              </NavLink>
            ))}
          </div>

          {!isLoggedIn ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to="/login"
                className="rounded-lg border border-gray-300 px-3 py-2 text-center text-sm font-medium text-gray-700"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="rounded-lg bg-[#282E6A] px-3 py-2 text-center text-sm font-medium text-white"
              >
                Get started
              </Link>
            </div>
          ) : (
            <div className="mt-3 space-y-1 border-t border-gray-100 pt-3">
              <p className="px-3 text-xs uppercase tracking-wide text-gray-400">Signed in as</p>
              <p className="px-3 text-sm font-semibold text-gray-800">{username}</p>
              <NavLink to="/notifications" className="block">Notifications</NavLink>
              <NavLink to="/mybookings" className="block">My Bookings</NavLink>
              <NavLink to="/editprofile" className="block">Edit Profile</NavLink>
              <button
                onClick={handleLogout}
                className="mt-1 w-full rounded-md px-3 py-2 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
